import { useState } from 'react';
import { Eye, EyeOff, Loader2, LogIn } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';

export const AuthForm = ({ mode = 'login', onSuccess, testId }) => {
  const { login, register } = useAuth();
  const [currentMode, setMode] = useState(mode);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const creating = currentMode === 'register';
  const field = (name) => ({ value: form[name], onChange: (e) => setForm({ ...form, [name]: e.target.value }) });

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (creating && form.password !== form.confirm) { setError('Passwords do not match.'); return; }
    if (new TextEncoder().encode(form.password).length > 72) { setError('Password must be no more than 72 UTF-8 bytes.'); return; }
    setBusy(true);
    const result = creating ? await register(form.email.trim(), form.password, form.name.trim()) : await login(form.email.trim(), form.password);
    setBusy(false);
    if (result.success) { setForm({ name: '', email: '', password: '', confirm: '' }); onSuccess(); }
    else setError(result.error);
  };

  return <div className="auth-form-content" data-testid={`${testId}-content`}>
    <h2 data-testid={`${testId}-heading`}>{creating ? 'Create your account' : 'Welcome back'}</h2>
    <p className="auth-subtitle" data-testid={`${testId}-description`}>{creating ? '30 questions each day with a free account.' : 'Sign in to your NextXus account.'}</p>
    <form onSubmit={submit} data-testid={`${testId}-form`}>
      <fieldset disabled={busy}>
        {creating && <label htmlFor={`${testId}-name`}>Name
          <Input id={`${testId}-name`} data-testid={`${testId}-name`} autoComplete="name" required maxLength={80} {...field('name')} />
        </label>}
        <label htmlFor={`${testId}-email`}>Email
          <Input id={`${testId}-email`} data-testid={`${testId}-email`} type="email" autoComplete="email" autoCapitalize="none" required {...field('email')} />
        </label>
        <label htmlFor={`${testId}-password`}>Password
          <div className="auth-password-field">
            <Input id={`${testId}-password`} data-testid={`${testId}-password`} type={visible ? 'text' : 'password'}
              autoComplete={creating ? 'new-password' : 'current-password'} required minLength={creating ? 8 : 1} maxLength={72} {...field('password')} />
            <button type="button" data-testid={`${testId}-password-toggle`} title={visible ? 'Hide password' : 'Show password'} aria-label={visible ? 'Hide password' : 'Show password'} aria-pressed={visible} onClick={() => setVisible(!visible)}>
              {visible ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>
        {creating && <>
          <p className="auth-password-note" data-testid={`${testId}-password-requirements`}>At least 8 characters; maximum 72 UTF-8 bytes.</p>
          <label htmlFor={`${testId}-confirm`}>Confirm password
            <Input id={`${testId}-confirm`} data-testid={`${testId}-confirm`} type={visible ? 'text' : 'password'} autoComplete="new-password" required {...field('confirm')} />
          </label>
        </>}
        {error && <p role="alert" className="auth-error" data-testid={`${testId}-error`}>{error}</p>}
        <Button type="submit" className="auth-submit" data-testid={`${testId}-submit`} disabled={busy}>
          {busy ? <Loader2 className="animate-spin" /> : <LogIn />} {busy ? 'Please wait…' : creating ? 'Create account' : 'Log in'}
        </Button>
      </fieldset>
    </form>
    <div className="auth-divider" aria-hidden="true">or</div>
    <Button type="button" variant="outline" className="auth-google" data-testid={testId === 'auth-modal' ? 'google-signin-button' : `${testId}-google`} disabled={busy}
      onClick={() => { window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/')}`; }}>
      Continue with Google
    </Button>
    <button type="button" className="auth-switch" data-testid={`${testId}-switch`} disabled={busy}
      onClick={() => { setMode(creating ? 'login' : 'register'); setError(''); setForm({ ...form, password: '', confirm: '' }); }}>
      {creating ? 'Already have an account? Log in' : 'New here? Create an account'}
    </button>
  </div>;
};